import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { ProgressBar } from "primereact/progressbar";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { TabView, TabPanel } from "primereact/tabview";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { Rating } from "primereact/rating";
import { MultiSelect } from "primereact/multiselect";
import { useToast } from "../../utils/toast";
import dynamicSurveyService, {
  DynamicRuleDto,
  DynamicRuleConditionDto,
  DynamicQuestionDto,
  DynamicSectionDto,
  DynamicSurveyDto,
} from "../../services/forms/dynamic.service";

const QUESTION_TYPES = [
  { label: "Serbest Metin", value: "text", icon: "pi pi-align-left", color: "var(--purple-500)" },
  { label: "Tarih", value: "date", icon: "pi pi-calendar", color: "var(--cyan-500)" },
  { label: "Sayı", value: "number", icon: "pi pi-hashtag", color: "var(--orange-500)" },
  { label: "Tek Seçmeli", value: "single_choice", icon: "pi pi-circle-fill", color: "var(--blue-500)" },
  { label: "Açılır Liste", value: "dropdown", icon: "pi pi-chevron-down", color: "var(--teal-500)" },
  { label: "Çok Seçmeli", value: "multiple_choice", icon: "pi pi-check-square", color: "var(--indigo-500)" },
  { label: "Derecelendirme (1-5)", value: "rating", icon: "pi pi-star-fill", color: "var(--yellow-500)" },
];

const TYPE_LABELS: Record<string, string> = {
  text: "Serbest Metin",
  single_choice: "Tek Seçmeli",
  dropdown: "Açılır Liste",
  multiple_choice: "Çok Seçmeli",
  rating: "Derecelendirme (1-5)",
  date: "Tarih",
  number: "Sayı",
};

const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const emptyChoice = (text = ""): { key: string; text: string } => ({ key: generateUUID(), text });

const emptyQuestion = (): DynamicQuestionDto => ({
  key: generateUUID(),
  text: "",
  question_type: "text",
  order: 0,
  choices: [],
  is_required: true,
  config: {},
});

const newSection = (order: number): DynamicSectionDto => ({
  key: generateUUID(),
  title: `Bölüm ${order + 1}`,
  description: "",
  order,
});

const toLocalDateTimeInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const DynamikAnketOlustur = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(0);

  // Anket bilgileri
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [accessMode, setAccessMode] = useState<"public" | "authenticated">("authenticated");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [responseLimit, setResponseLimit] = useState<number | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [questions, setQuestions] = useState<DynamicQuestionDto[]>([]);
  const [rules, setRules] = useState<DynamicRuleDto[]>([]);
  const [logicDialogVisible, setLogicDialogVisible] = useState(false);
  const [logicQuestionKey, setLogicQuestionKey] = useState<string | null>(null);
  const [logicSectionKey, setLogicSectionKey] = useState<string | null>(null);
  const [sections, setSections] = useState<DynamicSectionDto[]>([newSection(0)]);
  const [saving, setSaving] = useState(false);
  const [structureChangeConfirmationVisible, setStructureChangeConfirmationVisible] = useState(false);

  // Soru modal state
  const [soruDialogVisible, setSoruDialogVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<DynamicQuestionDto>(emptyQuestion());
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);

  // Önizleme state
  const [previewSectionKey, setPreviewSectionKey] = useState<string>("");
  const [previewAnswers, setPreviewAnswers] = useState<Record<number, any>>({});
  const [sectionInfoVisible, setSectionInfoVisible] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const dotsMenuRef = useRef<Menu>(null);

  // Edit modunda mevcut anketi yükle
  const { data: surveyData, isLoading: surveyLoading } = useQuery({
    queryKey: ["dynamicSurveyDetail", id],
    queryFn: () => dynamicSurveyService.getSurveyDetail(Number(id)),
    enabled: isEditMode,
  });

  const shareToken = surveyData?.result?.data?.share_token;
  const responseCount = surveyData?.result?.data?.response_count ?? 0;
  const hasResponses = responseCount > 0;
  const logicQuestion = questions.find((question) => question.key === logicQuestionKey);
  const logicSection = sections.find((section) => section.key === logicSectionKey);
  const logicRules = rules.map((rule, ruleIndex) => ({ rule, ruleIndex })).filter(({ rule }) =>
    logicSectionKey
      ? rule.source_section_key === logicSectionKey && rule.conditions.length === 0
      : rule.conditions[0]?.question_key === logicQuestionKey
  );

  useEffect(() => {
    if (surveyData?.result?.data) {
      const s = surveyData.result.data;
      setTitle(s.title);
      setDescription(s.description ?? "");
      setIsActive(s.is_active);
      setAccessMode(s.access_mode ?? "authenticated");
      setStartsAt(toLocalDateTimeInput(s.starts_at));
      setEndsAt(toLocalDateTimeInput(s.ends_at));
      setResponseLimit(s.response_limit ?? null);
      setIsAnonymous(!!s.is_anonymous);
      setSections(s.sections?.length ? s.sections.map((section) => ({ ...section })) : [newSection(0)]);
      setQuestions(s.questions ? s.questions.map((q) => ({ ...q, choices: q.choices ? [...q.choices] : [] })) : []);
      setRules(s.rules ? s.rules.map((rule) => ({ ...rule, conditions: [...rule.conditions] })) : []);
    }
  }, [surveyData]);

  useEffect(() => {
    if (sections.length > 0 && (!previewSectionKey || !sections.some((s) => s.key === previewSectionKey))) {
      setPreviewSectionKey(sections[0].key);
    }
  }, [sections, previewSectionKey]);

  // ── Soru modal işlemleri ──────────────────────────────────────────────
  const openAddQuestion = (sectionKey: string) => {
    setEditingQuestion({
      ...emptyQuestion(),
      order: questions.filter((question) => question.section_key === sectionKey).length,
      section_key: sectionKey,
      choices: [emptyChoice("Seçenek 1")],
    });
    setEditingQuestionIndex(null);
    setShowHelpText(false);
    setSoruDialogVisible(true);
  };

  const openEditQuestion = (index: number) => {
    const q = questions[index];
    setEditingQuestion({ ...q, choices: q.choices ? [...q.choices] : [] });
    setEditingQuestionIndex(index);
    setShowHelpText(Boolean(q.help_text));
    setSoruDialogVisible(true);
  };

  const saveQuestion = () => {
    if (!editingQuestion.text.trim()) {
      toast.show("Soru metni boş bırakılamaz.", "error");
      return;
    }
    if (
      ["single_choice", "dropdown", "multiple_choice"].includes(editingQuestion.question_type) &&
      (editingQuestion.choices?.filter((c) => c.text.trim()).length ?? 0) < 2
    ) {
      toast.show("Seçenekli sorularda en az 2 dolu seçenek gereklidir.", "error");
      return;
    }

    const cleaned = {
      ...editingQuestion,
      choices:
        ["text", "date", "number", "rating"].includes(editingQuestion.question_type)
          ? []
          : editingQuestion.choices?.filter((c) => c.text.trim()),
    };

    if (editingQuestionIndex === null) {
      setQuestions((prev) => [...prev, cleaned]);
    } else {
      setQuestions((prev) => prev.map((q, i) => (i === editingQuestionIndex ? cleaned : q)));
    }
    setSoruDialogVisible(false);
  };

  const removeQuestion = (index: number) => {
    const removedKey = questions[index]?.key;
    setQuestions((prev) => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })));
    if (removedKey) {
      setRules((previous) => previous.filter((rule) => rule.target_question_key !== removedKey && !rule.conditions.some((condition) => condition.question_key === removedKey)));
    }
  };

  const addSection = () => setSections((previous) => [...previous, newSection(previous.length)]);
  const moveSection = (index: number, direction: -1 | 1) =>
    setSections((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) return previous;
      const reordered = [...previous];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered.map((section, order) => ({ ...section, order }));
    });

  const moveQuestion = (index: number, direction: -1 | 1) =>
    setQuestions((previous) => {
      const sectionKey = previous[index]?.section_key;
      const sectionIndexes = previous
        .map((question, questionIndex) => (question.section_key === sectionKey ? questionIndex : -1))
        .filter((questionIndex) => questionIndex >= 0);
      const position = sectionIndexes.indexOf(index);
      const target = sectionIndexes[position + direction];
      if (target === undefined) return previous;
      const reordered = [...previous];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered.map((question, order) => ({ ...question, order }));
    });

  const updateSection = (key: string, patch: Partial<DynamicSectionDto>) =>
    setSections((previous) =>
      previous.map((section) => (section.key === key ? { ...section, ...patch } : section))
    );

  const removeSection = (key: string) => {
    if (sections.length < 2) {
      toast.show("Ankette en az bir bölüm bulunmalıdır.", "error");
      return;
    }
    const fallback = sections.find((section) => section.key !== key)!.key;
    setSections((previous) =>
      previous.filter((section) => section.key !== key).map((section, order) => ({ ...section, order }))
    );
    setQuestions((previous) =>
      previous.map((question) => ({
        ...question,
        section_key: question.section_key === key ? fallback : question.section_key,
      }))
    );
    setRules((previous) => previous
      .filter((rule) => rule.target_section_key !== key)
      .map((rule) => rule.source_section_key === key ? { ...rule, source_section_key: fallback } : rule));
  };

  // Seçenek işlemleri (modal içi)
  const addChoice = () => {
    setEditingQuestion((prev) => ({ ...prev, choices: [...(prev.choices ?? []), emptyChoice()] }));
  };

  const updateChoice = (cIndex: number, value: string) => {
    setEditingQuestion((prev) => {
      const choices = [...(prev.choices ?? [])];
      choices[cIndex] = { ...choices[cIndex], text: value };
      return { ...prev, choices };
    });
  };

  const removeChoice = (cIndex: number) => {
    const removedKey = editingQuestion.choices?.[cIndex]?.key;
    setEditingQuestion((prev) => ({
      ...prev,
      choices: prev.choices?.filter((_, i) => i !== cIndex),
    }));
    if (removedKey && editingQuestion.key) {
      setRules((previous) => previous.map((rule) => ({
        ...rule,
        conditions: rule.conditions.filter((condition) => condition.question_key !== editingQuestion.key || (condition.value !== removedKey && !(Array.isArray(condition.value) && condition.value.includes(removedKey)))),
      })).filter((rule) => rule.conditions.length > 0));
    }
  };

  const addRule = (sourceQuestionKey?: string) => {
    if (questions.length < 1) {
      toast.show("Kural eklemek için önce bir soru oluşturun.", "warn");
      return;
    }
    const source = questions.find((question) => question.key === sourceQuestionKey) ?? questions[0];
    const target = questions.find((question) => question.key !== source.key);
    const actionType: DynamicRuleDto["action_type"] = target ? "hide_question" : "submit";
    setRules((previous) => [
      ...previous,
      {
        priority: previous.length,
        source_section_key: source.section_key ?? null,
        match_all: true,
        conditions: [{ question_key: source.key!, operator: "answered" }],
        action_type: actionType,
        target_question_key: actionType === "hide_question" ? target?.key : null,
        target_section_key: null,
      },
    ]);
  };

  const addSectionRule = (sourceSectionKey: string) => {
    if (rules.some((rule) => rule.source_section_key === sourceSectionKey && rule.conditions.length === 0)) return;
    const target = sections.find((section) => section.key !== sourceSectionKey);
    if (!target) return;
    setRules((previous) => [
      ...previous,
      {
        priority: previous.length,
        source_section_key: sourceSectionKey,
        match_all: true,
        conditions: [],
        action_type: "goto_section",
        target_question_key: null,
        target_section_key: target.key,
      },
    ]);
  };

  const updateRule = (index: number, patch: Partial<DynamicRuleDto>) =>
    setRules((previous) => previous.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  const moveRule = (index: number, direction: -1 | 1, ownerQuestionKey: string | null, ownerSectionKey: string | null) => setRules((previous) => {
    const groupedIndexes = previous.map((rule, ruleIndex) => {
      const belongs = ownerSectionKey
        ? rule.source_section_key === ownerSectionKey && rule.conditions.length === 0
        : rule.conditions[0]?.question_key === ownerQuestionKey;
      return belongs ? ruleIndex : -1;
    }).filter((ruleIndex) => ruleIndex >= 0);
    const groupedPosition = groupedIndexes.indexOf(index);
    const target = groupedIndexes[groupedPosition + direction];
    if (target === undefined) return previous;
    const reordered = [...previous];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    return reordered.map((rule, priority) => ({ ...rule, priority }));
  });

  const updateRuleCondition = (ruleIndex: number, conditionIndex: number, patch: Partial<DynamicRuleConditionDto>) =>
    setRules((previous) => previous.map((rule, i) => i === ruleIndex ? {
      ...rule,
      conditions: rule.conditions.map((condition, j) => j === conditionIndex ? { ...condition, ...patch } : condition),
    } : rule));

  const addRuleCondition = (ruleIndex: number) => {
    const source = questions.find((question) => question.key === logicQuestionKey) ?? questions[0];
    if (!source?.key) return;
    setRules((previous) => previous.map((rule, i) => i === ruleIndex ? {
      ...rule,
      conditions: [...rule.conditions, { question_key: source.key!, operator: "answered" }],
    } : rule));
  };

  const toggleChoiceCorrect = (cIndex: number) => {
    setEditingQuestion((prev) => {
      const isSingle = prev.question_type === "single_choice" || prev.question_type === "dropdown";
      const choices = (prev.choices ?? []).map((choice, i) => {
        if (i === cIndex) {
          return { ...choice, is_correct: !choice.is_correct };
        }
        if (isSingle) {
          return { ...choice, is_correct: false };
        }
        return choice;
      });
      return { ...prev, choices };
    });
  };

  // Anket kaydetme
  const structureSignature = (sectionData: DynamicSectionDto[], questionData: DynamicQuestionDto[], ruleData: DynamicRuleDto[]) =>
    JSON.stringify({
      sections: sectionData.map(({ key, title, description, order }) => ({ key, title, description, order })),
      questions: questionData.map(({ key, text, help_text, is_required, question_type, order, section_key, config, choices }) => ({
        key, text, help_text, is_required, question_type, order, section_key, config,
        choices: (choices ?? []).map(({ key: choiceKey, text: choiceText, is_correct }) => ({ key: choiceKey, text: choiceText, is_correct })),
      })),
      rules: ruleData.map(({ source_section_key, priority, match_all, conditions, action_type, target_question_key, target_section_key }) => ({
        source_section_key, priority, match_all, conditions, action_type, target_question_key, target_section_key,
      })),
    });

  const handleSave = async (confirmedStructureChange = false) => {
    const savedSurvey = surveyData?.result?.data;
    const structureChanged = !!savedSurvey && structureSignature(sections, questions, rules) !== structureSignature(
      savedSurvey.sections ?? [], savedSurvey.questions ?? [], savedSurvey.rules ?? []
    );
    if (hasResponses && structureChanged && !confirmedStructureChange) {
      setStructureChangeConfirmationVisible(true);
      return;
    }
    if (!title.trim()) {
      toast.show("Anket başlığı zorunludur.", "error");
      return;
    }
    if (questions.length === 0) {
      toast.show("En az bir soru eklemelisiniz.", "error");
      return;
    }
    if (sections.some((section) => !section.title.trim())) {
      toast.show("Bölüm başlıkları boş bırakılamaz.", "error");
      return;
    }
    for (const question of questions) {
      if (question.question_type === "number" && question.config?.min != null && question.config?.max != null && question.config.min > question.config.max) {
        toast.show(`“${question.text}” sorusunda en düşük değer en yüksek değerden büyük olamaz.`, "error");
        return;
      }
    }
    for (const rule of rules) {
      const missingConditionValue = rule.conditions.some((condition) => !["answered", "not_answered"].includes(condition.operator) && (condition.value == null || condition.value === "" || (Array.isArray(condition.value) && condition.value.length === 0)));
      const missingTarget = (rule.action_type === "hide_question" && !rule.target_question_key) || (rule.action_type === "goto_section" && !rule.target_section_key);
      const sectionEndRule = !rule.conditions.length && !!rule.source_section_key && ["goto_section", "submit"].includes(rule.action_type);
      if ((!rule.conditions.length && !sectionEndRule) || missingConditionValue || missingTarget) {
        toast.show("Koşullu mantıktaki koşul değerlerini ve eylem hedeflerini tamamlayın.", "error");
        setActiveTab(1);
        return;
      }
    }

    const payload: DynamicSurveyDto = {
      title,
      description,
      is_active: isActive,
      access_mode: accessMode,
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      response_limit: responseLimit,
      is_anonymous: isAnonymous,
    };

    if (!hasResponses || structureChanged) {
      payload.sections = sections.map((section, order) => ({ ...section, order }));
      payload.questions = questions.map((q, i) => ({ ...q, order: i }));
      payload.rules = rules.map((rule, i) => ({ ...rule, priority: i }));
    }

    setSaving(true);
    try {
      const res = isEditMode
        ? await dynamicSurveyService.updateSurvey(Number(id), payload)
        : await dynamicSurveyService.createSurvey(payload);

      if (res.result && !res.result.error) {
        toast.show(isEditMode ? "Anket güncellendi." : "Anket oluşturuldu.", "success");
        if (!isEditMode) {
          navigate("/admin/dinamik-anket-yonetim");
        }
      } else {
        let errMsg = "Bir hata oluştu.";
        if (res.result?.error) {
          if (typeof res.result.error === "string") {
            errMsg = res.result.error;
          } else if (typeof res.result.error === "object") {
            const parts: string[] = [];
            for (const [key, val] of Object.entries(res.result.error)) {
              const text = Array.isArray(val) ? val.join(" ") : String(val);
              const label = key === "response_limit" ? "Yanıt sınırı: " : key === "ends_at" ? "Bitiş zamanı: " : "";
              parts.push(`${label}${text}`);
            }
            errMsg = parts.join(" | ") || "Geçersiz veri.";
          }
        }
        toast.show(errMsg, "error");
      }
    } catch {
      toast.show("Bir hata oluştu.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (isEditMode && surveyLoading) {
    return (
      <div className="flex flex-column justify-content-center align-items-center" style={{ minHeight: "380px" }}>
        <ProgressSpinner />
        <span className="text-500 mt-3 font-medium">Anket detayları yükleniyor...</span>
      </div>
    );
  }

  // http.service returns failed requests as a result envelope instead of throwing,
  // so a missing survey must not fall through to the blank create-form state.
  if (isEditMode && !surveyData?.result?.data) {
    return (
      <div className="p-3 md:p-4" style={{ maxWidth: "720px", margin: "0 auto" }}>
        <Card className="border-round-xl shadow-1">
          <div className="flex flex-column align-items-center text-center gap-3 p-3">
            <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: "2rem" }} />
            <h2 className="text-900 text-xl m-0">Anket yüklenemedi</h2>
            <p className="text-600 m-0">
              Anket bulunamadı veya bilgileri alınamadı. Listeye dönüp yeniden deneyin.
            </p>
            <Button
              label="Anket Listesine Dön"
              icon="pi pi-arrow-left"
              onClick={() => navigate("/admin/dinamik-anket-yonetim")}
            />
          </div>
        </Card>
      </div>
    );
  }

  // Preview hesaplamaları
  const currentPreviewSection = sections.find((s) => s.key === previewSectionKey) ?? sections[0];
  const previewQuestions = questions.filter((q) => q.section_key === currentPreviewSection?.key);
  const currentSectionIndex = sections.findIndex((s) => s.key === previewSectionKey);

  return (
    <div className="p-3 md:p-4" style={{ maxWidth: "1080px", margin: "0 auto" }}>
      {/* ── Üst Başlık & Eylem Çubuğu ─────────────────────────────────── */}
      <div className="surface-card border-round-xl shadow-1 p-3 md:p-4 mb-4 border-1 surface-border">
        <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
          <div>
            <Button
              icon="pi pi-arrow-left"
              label="Anket Listesine Dön"
              text
              severity="secondary"
              className="p-0 mb-2 font-medium"
              onClick={() => navigate("/admin/dinamik-anket-yonetim")}
            />
            <div className="flex align-items-center gap-2 flex-wrap">
              <h1 className="text-900 font-bold text-2xl m-0">
                {isEditMode ? "Anketi Düzenle" : "Yeni Anket Oluştur"}
              </h1>
              <Tag
                value={isActive ? "Yayında (Aktif)" : "Pasif / Taslak"}
                severity={isActive ? "success" : "warning"}
                icon={isActive ? "pi pi-check" : "pi pi-eye-slash"}
              />
              {hasResponses && (
                <Tag
                  value={`${responseCount} Yanıt Alındı`}
                  severity="info"
                  icon="pi pi-users"
                />
              )}
            </div>
            <p className="text-500 text-sm mt-1 mb-0">
              Çok bölümlü, dallanma mantıklı ve dinamik kurallara sahip profesyonel anketler tasarlayın.
            </p>
          </div>

          <div className="flex align-items-center gap-2 w-full md:w-auto justify-content-end flex-wrap">
            {isEditMode && (
              <Button
                label="Analiz & Rapor"
                icon="pi pi-chart-bar"
                outlined
                severity="info"
                onClick={() => navigate(`/admin/dinamik-anket-analiz/${id}`)}
              />
            )}
            <Button
              label="İptal"
              icon="pi pi-times"
              outlined
              severity="secondary"
              onClick={() => navigate("/admin/dinamik-anket-yonetim")}
              disabled={saving}
            />
            <Button
              label={isEditMode ? "Güncelle" : "Anketi Oluştur"}
              icon="pi pi-save"
              loading={saving}
              onClick={() => { void handleSave(); }}
              className="p-button-primary font-semibold shadow-1"
            />
          </div>
        </div>

        {/* İstatistik Rozetleri */}
        <div className="flex align-items-center gap-3 mt-3 pt-3 border-top-1 surface-border text-sm text-600 flex-wrap">
          <div className="flex align-items-center gap-1">
            <i className="pi pi-folder text-primary" />
            <span className="font-semibold text-900">{sections.length}</span> Bölüm
          </div>
          <span>•</span>
          <div className="flex align-items-center gap-1">
            <i className="pi pi-list text-primary" />
            <span className="font-semibold text-900">{questions.length}</span> Soru
          </div>
          <span>•</span>
          <div className="flex align-items-center gap-1">
            <i className="pi pi-users text-primary" />
            <span>Kontenjan: </span>
            <span className="font-semibold text-900">{responseLimit ? `${responseLimit} kişi` : "Sınırsız"}</span>
          </div>
          <span>•</span>
          <div className="flex align-items-center gap-1">
            <i className={accessMode === "public" ? "pi pi-globe text-green-500" : "pi pi-lock text-orange-500"} />
            <span>{accessMode === "public" ? "Bağlantıya Açık" : "Giriş Zorunlu"}</span>
          </div>
        </div>
      </div>

      {/* ── Sekmeli Görünüm ─────────────────────────────────────────── */}
      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} className="custom-tabview">
        {/* SEKME 1: GENEL VE YAYIN AYARLARI */}
        <TabPanel header="Genel & Yayın Ayarları" leftIcon="pi pi-cog mr-2">
          <div className="grid mt-2">
            {/* Sol Kart: Temel Anket Bilgileri */}
            <div className="col-12 lg:col-7">
              <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-file-edit text-primary font-bold text-xl" />
                  <h3 className="text-900 font-semibold text-lg m-0">Temel Bilgiler</h3>
                </div>

                <div className="flex flex-column gap-3">
                  <div>
                    <label className="block text-900 font-medium mb-1">
                      Anket Başlığı <span className="text-red-500">*</span>
                    </label>
                    <InputText
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full"
                      placeholder="Örn: 2026 Müşteri Memnuniyeti Araştırması"
                    />
                    <small className="text-500">Katılımcıların ilk göreceği ana başlık.</small>
                  </div>

                  <div>
                    <label className="block text-900 font-medium mb-1">Açıklama / Yönerge</label>
                    <InputTextarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full"
                      rows={4}
                      placeholder="Katılımcılara anketin amacı ve doldurma süresi hakkında kısa bilgi verin..."
                      autoResize
                    />
                  </div>

                  <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-center justify-content-between">
                    <div>
                      <span className="block font-semibold text-900">Yayın Durumu</span>
                      <small className="text-500">
                        {isActive
                          ? "Anket aktif ve yanıt kabul etmeye hazır."
                          : "Anket taslak/pasif modda, katılımcılara gösterilmez."}
                      </small>
                    </div>
                    <InputSwitch checked={isActive} onChange={(e) => setIsActive(e.value)} />
                  </div>
                </div>
              </Card>
            </div>

            {/* Sağ Kart: Erişim & Katılım Kuralları */}
            <div className="col-12 lg:col-5">
              <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-shield text-primary font-bold text-xl" />
                  <h3 className="text-900 font-semibold text-lg m-0">Erişim & Katılım Kuralları</h3>
                </div>

                <div className="flex flex-column gap-3">
                  {/* Kimler Yanıtlayabilir */}
                  <div>
                    <label className="block text-900 font-medium mb-2">Kimler Yanıtlayabilir?</label>
                    <div className="flex flex-column gap-2">
                      <div
                        className={`p-3 border-round-lg cursor-pointer border-1 transition-duration-150 flex align-items-start gap-2 ${
                          accessMode === "public"
                            ? "surface-card border-primary"
                            : "surface-50 surface-border"
                        }`}
                        onClick={() => setAccessMode("public")}
                      >
                        <RadioButton
                          inputId="access-public"
                          value="public"
                          checked={accessMode === "public"}
                          onChange={(e) => setAccessMode(e.value)}
                        />
                        <div>
                          <label htmlFor="access-public" className="font-semibold text-900 cursor-pointer block">
                            Bağlantıya Sahip Herkes
                          </label>
                          <small className="text-500">Giriş yapmadan, paylaşım bağlantısını açan herkes doldurabilir.</small>
                        </div>
                      </div>

                      <div
                        className={`p-3 border-round-lg cursor-pointer border-1 transition-duration-150 flex align-items-start gap-2 ${
                          accessMode === "authenticated"
                            ? "surface-card border-primary"
                            : "surface-50 surface-border"
                        }`}
                        onClick={() => setAccessMode("authenticated")}
                      >
                        <RadioButton
                          inputId="access-auth"
                          value="authenticated"
                          checked={accessMode === "authenticated"}
                          onChange={(e) => setAccessMode(e.value)}
                        />
                        <div>
                          <label htmlFor="access-auth" className="font-semibold text-900 cursor-pointer block">
                            Yalnızca Giriş Yapmış Kullanıcılar
                          </label>
                          <small className="text-500">Yanıtlamak için sisteme üye girişi gereklidir.</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider className="my-1" />

                  {/* Tarihler */}
                  <div className="grid">
                    <div className="col-12 sm:col-6">
                      <label className="block text-900 font-medium mb-1 text-sm">Başlangıç Zamanı</label>
                      <InputText
                        type="datetime-local"
                        value={startsAt}
                        onChange={(e) => setStartsAt(e.target.value)}
                        className="w-full text-sm"
                      />
                    </div>
                    <div className="col-12 sm:col-6">
                      <label className="block text-900 font-medium mb-1 text-sm">Bitiş Zamanı</label>
                      <InputText
                        type="datetime-local"
                        value={endsAt}
                        onChange={(e) => setEndsAt(e.target.value)}
                        className="w-full text-sm"
                      />
                    </div>
                  </div>

                  {/* Yanıt Üst Sınırı (Kontenjan) */}
                  <div>
                    <label className="block text-900 font-medium mb-1 text-sm">
                      Yanıt Üst Sınırı (Kontenjan)
                      <i
                        className="pi pi-info-circle text-500 ml-1"
                        title="Bu sayıya ulaşıldığında anket yeni katılımlara otomatik olarak kapanır."
                      />
                    </label>
                    <InputNumber
                      value={responseLimit}
                      onValueChange={(e) => setResponseLimit(e.value ?? null)}
                      min={1}
                      placeholder="Örn: 100 (Boş = Sınırsız)"
                      className="w-full"
                      inputClassName="w-full"
                      suffix=" kişi"
                    />
                    <small className="text-500 block mt-1">
                      Maksimum yanıt sayısıdır. Kota koymamak için boş bırakınız.
                    </small>
                  </div>

                  {/* Anonim Yanıt */}
                  <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-center justify-content-between">
                    <div>
                      <span className="block font-semibold text-900">Anonim Yanıt</span>
                      <small className="text-500">
                        {isAnonymous
                          ? "Katılımcının hesap bilgisi saklanmaz."
                          : accessMode === "public"
                            ? "Giriş yapanların hesap bilgisi kaydedilir; giriş yapmadan katılanların kimliği belirlenemez."
                            : "Giriş yapan katılımcıların hesap bilgisi kaydedilir."}
                      </small>
                    </div>
                    <InputSwitch checked={isAnonymous} onChange={(e) => setIsAnonymous(e.value)} />
                  </div>
                </div>
              </Card>
            </div>

            {/* Paylaşım Bağlantısı Kartı (Edit modunda) */}
            {isEditMode && shareToken && (
              <div className="col-12">
                <Card className="border-round-xl shadow-1 surface-card border-1 surface-border">
                  <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
                    <div>
                      <span className="font-semibold text-900 text-lg block">Paylaşım Bağlantısı</span>
                      <small className="text-500">
                        {accessMode === "public"
                          ? "Bu bağlantıyı kopyalayarak herkese gönderebilir veya sitenize ekleyebilirsiniz."
                          : "Bu bağlantı ile yalnızca giriş yapmış kullanıcılar ankete katılabilir."}
                      </small>
                    </div>
                    <div className="flex align-items-center gap-2 w-full md:w-auto">
                      <InputText
                        readOnly
                        value={`${window.location.origin}/anket/${shareToken}`}
                        className="w-full md:w-26rem surface-100 font-mono text-sm"
                      />
                      <Button
                        icon="pi pi-copy"
                        label="Kopyala"
                        severity="secondary"
                        onClick={() => {
                          if (shareToken) {
                            void navigator.clipboard.writeText(`${window.location.origin}/anket/${shareToken}`);
                            toast.show("Paylaşım bağlantısı panoya kopyalandı.", "info");
                          }
                        }}
                      />
                      <Button
                        icon="pi pi-external-link"
                        tooltip="Yeni sekmede aç"
                        outlined
                        severity="secondary"
                        onClick={() => window.open(`/anket/${shareToken}`, "_blank")}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </TabPanel>

        {/* SEKME 2: BÖLÜMLER & SORULAR */}
        <TabPanel header={`Bölümler & Sorular (${questions.length})`} leftIcon="pi pi-list mr-2">
          {hasResponses && (
            <div className="surface-100 border-round-xl p-3 mb-4 flex align-items-center gap-3 border-left-3 border-orange-500 shadow-1">
              <i className="pi pi-exclamation-triangle text-orange-500 text-2xl" />
              <div>
                <span className="font-semibold text-900 block">Yanıtlar varken soru yapısı düzenlenebilir</span>
                <span className="text-600 text-sm">
                  Bu ankete <strong>{responseCount}</strong> yanıt verilmiştir. Soru, seçenek, bölüm veya akış kurallarını değiştirmek mevcut yanıtların anlamını etkileyebilir; kayıtlı cevap içerikleri kaybolabilir. Yapısal bir değişiklik kaydedilirken ayrıca onayınız istenir.
                </span>
              </div>
            </div>
          )}

          <div className="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="text-900 font-bold text-xl m-0">Anket Akışı ve Sorular</h3>
              <p className="text-500 text-sm m-0">Bölümleri düzenleyin, sorular ekleyin ve yanıt bazlı dallanma kuralları belirleyin.</p>
            </div>
            <div className="flex align-items-center gap-2">
              <Button
                type="button"
                icon="pi pi-question-circle"
                label="Bölüm Nedir?"
                size="small"
                outlined
                severity="info"
                className="font-medium"
                tooltip="Bölümler ve akış mantığı ne işe yarar? Tıklayarak inceleyin."
                tooltipOptions={{ position: "top" }}
                onClick={() => setSectionInfoVisible(true)}
              />
              <Button
                label="Yeni Bölüm Ekle"
                icon="pi pi-plus"
                outlined
                className="font-semibold"
                onClick={addSection}
              />
            </div>
          </div>

          <div className="flex flex-column gap-4 mb-4">
            {sections.map((section, sectionIndex) => {
              const sectionQuestions = questions.filter((q) => q.section_key === section.key);

              return (
                <div
                  key={section.key}
                  className="surface-card border-round-xl shadow-1 border-1 surface-border overflow-hidden"
                >
                  {/* Bölüm Başlığı & Kontroller */}
                  <div
                    className="p-3 md:p-4 surface-50 border-bottom-1 surface-border flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3"
                  >
                    <div className="flex-1 w-full">
                      <div className="flex align-items-center gap-2 mb-2">
                        <Tag
                          value={`Bölüm ${sectionIndex + 1}`}
                          className="font-bold bg-primary text-white text-xs px-2 py-1"
                        />
                        <span className="text-500 text-xs font-medium">({sectionQuestions.length} soru)</span>
                      </div>
                      <InputText
                        value={section.title}
                        onChange={(e) => updateSection(section.key, { title: e.target.value })}
                        placeholder="Bölüm Başlığı"
                        className="w-full font-semibold text-lg"
                      />
                      <InputTextarea
                        value={section.description ?? ""}
                        onChange={(e) => updateSection(section.key, { description: e.target.value })}
                        className="w-full mt-2 text-sm"
                        rows={1}
                        placeholder="Bölüm açıklaması (isteğe bağlı)..."
                        autoResize
                      />
                    </div>

                    <div className="flex align-items-center gap-1">
                        <Button
                          icon="pi pi-arrow-up"
                          rounded
                          text
                          severity="secondary"
                          tooltip="Bölümü yukarı taşı"
                          disabled={sectionIndex === 0}
                          onClick={() => moveSection(sectionIndex, -1)}
                        />
                        <Button
                          icon="pi pi-arrow-down"
                          rounded
                          text
                          severity="secondary"
                          tooltip="Bölümü aşağı taşı"
                          disabled={sectionIndex === sections.length - 1}
                          onClick={() => moveSection(sectionIndex, 1)}
                        />
                        <Button
                          icon="pi pi-trash"
                          rounded
                          text
                          severity="danger"
                          tooltip="Bölümü sil"
                          onClick={() => removeSection(section.key)}
                        />
                    </div>
                  </div>

                  <div className="px-3 md:px-4 py-2 surface-100 border-bottom-1 surface-border flex align-items-center justify-content-between gap-2 text-sm">
                    <span className="text-600 font-medium">Bölüm geçişleri ortak akış kurallarından yönetilir.</span>
                    <Button
                      label={`Bölüm akışı${rules.filter((rule) => rule.source_section_key === section.key && !rule.conditions.length).length ? ` (${rules.filter((rule) => rule.source_section_key === section.key && !rule.conditions.length).length})` : ""}`}
                      icon="pi pi-directions"
                      outlined
                      size="small"
                      disabled={sections.length < 2}
                      onClick={() => {
                        setLogicQuestionKey(null);
                        setLogicSectionKey(section.key);
                        setLogicDialogVisible(true);
                      }}
                    />
                  </div>

                  {/* Bölüm Soruları */}
                  <div className="p-3 md:p-4 flex flex-column gap-3">
                    {sectionQuestions.length === 0 ? (
                      <div className="p-4 border-2 border-dashed surface-border border-round-xl text-center surface-50">
                        <i className="pi pi-question-circle text-400 text-3xl mb-2" />
                        <p className="text-600 font-medium m-0 mb-3">Bu bölüme henüz soru eklenmedi.</p>
                        <Button
                            label="Bu Bölüme İlk Soruyu Ekle"
                            icon="pi pi-plus"
                            size="small"
                            onClick={() => openAddQuestion(section.key)}
                          />
                      </div>
                    ) : (
                      questions
                        .map((q, index) => ({ q, index }))
                        .filter(({ q }) => q.section_key === section.key)
                        .map(({ q, index }) => (
                          <div
                            key={q.id ?? `question-${index}`}
                            className="p-3 surface-card border-round-xl border-1 surface-border shadow-1 hover:shadow-2 transition-duration-150"
                            style={{ borderLeft: "5px solid var(--primary-color)" }}
                          >
                            <div className="flex align-items-start justify-content-between gap-2">
                              <div className="flex align-items-start gap-3 flex-1">
                                <span
                                  className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0 text-white shadow-1"
                                  style={{
                                    width: "30px",
                                    height: "30px",
                                    minWidth: "30px",
                                    background: "var(--primary-color)",
                                  }}
                                >
                                  {index + 1}
                                </span>
                                <div className="flex-1">
                                  <div className="flex align-items-center gap-2 flex-wrap mb-1">
                                    <Tag
                                      value={TYPE_LABELS[q.question_type]}
                                      icon={
                                        QUESTION_TYPES.find((t) => t.value === q.question_type)?.icon ??
                                        "pi pi-question"
                                      }
                                      severity="info"
                                      style={{ fontSize: "0.75rem" }}
                                    />
                                    {q.is_required && (
                                      <Tag value="Zorunlu" severity="warning" style={{ fontSize: "0.75rem" }} />
                                    )}
                                  </div>

                                  <p className="text-900 font-medium m-0 text-base" style={{ lineHeight: "1.5" }}>
                                    {q.text}
                                  </p>

                                  {q.help_text && (
                                    <small className="text-500 italic block mt-1">
                                      <i className="pi pi-info-circle mr-1 text-xs" />
                                      {q.help_text}
                                    </small>
                                  )}

                                  {["single_choice", "dropdown", "multiple_choice"].includes(q.question_type) &&
                                    (q.choices?.length ?? 0) > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {q.choices?.map((c, ci) => (
                                          <span
                                            key={ci}
                                            className="surface-100 border-round px-2 py-1 text-xs text-700 border-1 surface-border flex align-items-center gap-1"
                                          >
                                            <i
                                              className={
                                                q.question_type === "multiple_choice"
                                                  ? "pi pi-check-square text-primary text-xs"
                                                  : "pi pi-circle-fill text-primary text-xs"
                                              }
                                            />
                                            <span className="font-medium">{c.text}</span>
                                            {c.is_correct && (
                                              <Tag
                                                value="✓ Doğru Cevap"
                                                severity="success"
                                                style={{ fontSize: "0.65rem", padding: "0.1rem 0.35rem" }}
                                              />
                                            )}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                  {q.question_type === "rating" && (
                                    <div className="flex align-items-center gap-1 mt-2 text-yellow-500 text-xs">
                                      {[1, 2, 3, 4, 5].map((s) => (
                                        <i key={s} className="pi pi-star-fill" />
                                      ))}
                                      <span className="text-500 ml-1 text-xs">(1-5 Puanlama)</span>
                                    </div>
                                  )}

                                  {q.question_type === "text" && (
                                    <div className="mt-2 text-500 text-xs italic">
                                      <i className="pi pi-align-left mr-1" />
                                      Katılımcı serbest metin girecek
                                    </div>
                                  )}
                                  {q.question_type === "date" && <div className="mt-2 text-500 text-xs"><i className="pi pi-calendar mr-1" />Katılımcı bir tarih seçecek.</div>}
                                  {q.question_type === "number" && <div className="mt-2 text-500 text-xs"><i className="pi pi-hashtag mr-1" />Katılımcı sayısal değer girecek{q.config?.min != null || q.config?.max != null ? ` (${q.config.min ?? "…"} – ${q.config.max ?? "…"})` : ""}.</div>}
                                </div>
                              </div>

                              <div className="flex align-items-center gap-1 flex-wrap">
                                  <Button
                                    icon="pi pi-share-alt"
                                    label={`Mantık${rules.filter((rule) => rule.conditions[0]?.question_key === q.key).length ? ` (${rules.filter((rule) => rule.conditions[0]?.question_key === q.key).length})` : ""}`}
                                    outlined
                                    size="small"
                                    tooltip="Bu soruya bağlı koşulları düzenle"
                                    disabled={!q.key}
                                    onClick={() => { setLogicQuestionKey(q.key ?? null); setLogicSectionKey(null); setLogicDialogVisible(true); }}
                                  />
                                  <>
                                  <Button
                                    icon="pi pi-arrow-up"
                                    rounded
                                    text
                                    severity="secondary"
                                    tooltip="Yukarı taşı"
                                    onClick={() => moveQuestion(index, -1)}
                                  />
                                  <Button
                                    icon="pi pi-arrow-down"
                                    rounded
                                    text
                                    severity="secondary"
                                    tooltip="Aşağı taşı"
                                    onClick={() => moveQuestion(index, 1)}
                                  />
                                  <Button
                                    icon="pi pi-pencil"
                                    rounded
                                    text
                                    severity="warning"
                                    tooltip="Düzenle"
                                    onClick={() => openEditQuestion(index)}
                                  />
                                  <Button
                                    icon="pi pi-trash"
                                    rounded
                                    text
                                    severity="danger"
                                    tooltip="Sil"
                                    onClick={() => removeQuestion(index)}
                                  />
                                  </>
                              </div>
                            </div>
                          </div>
                        ))
                    )}

                    <Button
                        label="Bu Bölüme Soru Ekle"
                        icon="pi pi-plus"
                        outlined
                        className="w-full mt-2 border-dashed font-semibold"
                        onClick={() => openAddQuestion(section.key)}
                      />
                  </div>
                </div>
              );
            })}
          </div>
          <Dialog
            header={logicSection ? `Bölüm akışı: ${logicSection.title}` : logicQuestion ? `Koşullu mantık: ${logicQuestion.text || "Başlıksız soru"}` : "Koşullu mantık"}
            visible={logicDialogVisible}
            onHide={() => setLogicDialogVisible(false)}
            style={{ width: "min(900px, 96vw)" }}
            modal
          >
            <div className="mb-3">
              <p className="text-500 text-sm m-0">Bölüm atlama, anketi bitirme ve soruyu gizleme kuralları aynı akışta yönetilir. Sorulara bağlı kurallar yanıt koşullarını; bölüm kuralları ise bölüm tamamlandığında yapılacak geçişi tanımlar.</p>
            </div>
            <div className="flex align-items-center justify-content-between gap-3 flex-wrap mb-2">
              <div>
                <h3 className="text-900 font-bold text-lg m-0">{logicSection ? "Bölüm tamamlanınca" : "Bu soruya bağlı kurallar"}</h3>
                <p className="text-500 text-sm mb-0 mt-1">Koşullar öncelik sırasına göre değerlendirilir.</p>
              </div>
              <Button label="Kural Ekle" icon="pi pi-plus" onClick={() => logicSectionKey ? addSectionRule(logicSectionKey) : addRule(logicQuestionKey ?? undefined)} disabled={logicSectionKey ? logicRules.length > 0 : questions.length < 1 || !logicQuestionKey} />
            </div>
            {hasResponses && <div className="surface-100 border-round p-3 mt-3 text-600">Kural değişiklikleri mevcut yanıtların yorumlanmasını etkileyebilir.</div>}
            {logicRules.length === 0 && <div className="surface-50 border-round p-4 mt-3 text-center text-500">Bu soruya bağlı henüz kural yok.</div>}
            <div className="flex flex-column gap-3 mt-3">
              {logicRules.map(({ rule, ruleIndex }) => {
                const isSectionRule = !rule.conditions.length && !!rule.source_section_key;
                const actionOptions = isSectionRule
                  ? [{ label: "Bölüme git", value: "goto_section" }, { label: "Anketi bitir", value: "submit" }]
                  : [{ label: "Soruyu gizle", value: "hide_question" }, { label: "Bölüme git", value: "goto_section" }, { label: "Anketi bitir", value: "submit" }];
                return (
                <div key={rule.id ?? `rule-${ruleIndex}`} className="surface-50 border-1 surface-border border-round-lg p-3">
                  <div className="flex align-items-center justify-content-between gap-2 mb-3">
                    <div className="flex align-items-center gap-2">
                      <Tag value={`Öncelik ${ruleIndex + 1}`} severity="info" />
                      <span className="text-900 font-semibold">{isSectionRule ? "Bölüm tamamlandığında" : "Eğer koşullar eşleşirse"}</span>
                    </div>
                    <div className="flex align-items-center">
                      {!isSectionRule && <Button icon="pi pi-arrow-up" text rounded severity="secondary" aria-label="Kuralı önce taşı" disabled={logicRules.findIndex((item) => item.ruleIndex === ruleIndex) === 0} onClick={() => moveRule(ruleIndex, -1, logicQuestionKey, logicSectionKey)} />}
                      {!isSectionRule && <Button icon="pi pi-arrow-down" text rounded severity="secondary" aria-label="Kuralı sonra taşı" disabled={logicRules.findIndex((item) => item.ruleIndex === ruleIndex) === logicRules.length - 1} onClick={() => moveRule(ruleIndex, 1, logicQuestionKey, logicSectionKey)} />}
                      <Button icon="pi pi-trash" text rounded severity="danger" aria-label="Kuralı sil" onClick={() => setRules((previous) => previous.filter((_, i) => i !== ruleIndex))} />
                    </div>
                  </div>
                  {rule.conditions.length > 0 && <div className="flex align-items-center gap-2 mb-3">
                    <span className="text-sm text-700">Koşul grubu:</span>
                    <Dropdown value={rule.match_all ? "all" : "any"} options={[{ label: "Tüm koşullar (VE)", value: "all" }, { label: "Herhangi biri (VEYA)", value: "any" }]} onChange={(e) => updateRule(ruleIndex, { match_all: e.value === "all" })} className="w-16rem" />
                  </div>}
                  <div className="flex flex-column gap-2">
                    {rule.conditions.map((condition, conditionIndex) => {
                      const sourceQuestion = questions.find((question) => question.key === condition.question_key);
                      const isChoice = ["single_choice", "dropdown", "multiple_choice"].includes(sourceQuestion?.question_type ?? "");
                      const hasValue = !["answered", "not_answered"].includes(condition.operator);
                      const operatorOptions = isChoice
                        ? [{ label: "şu seçeneği içeriyorsa", value: "contains" }, { label: "şuna eşitse", value: "equals" }, { label: "şunların tümünü içeriyorsa", value: "contains_all" }, { label: "şunlardan en az birini içeriyorsa", value: "contains_any" }, { label: "yanıtlandıysa", value: "answered" }, { label: "yanıtlanmadıysa", value: "not_answered" }]
                        : sourceQuestion?.question_type === "number" || sourceQuestion?.question_type === "rating"
                          ? [{ label: "eşitse", value: "equals" }, { label: "büyüktür", value: "greater_than" }, { label: "en az", value: "greater_or_equal" }, { label: "küçüktür", value: "less_than" }, { label: "en çok", value: "less_or_equal" }, { label: "yanıtlandıysa", value: "answered" }, { label: "yanıtlanmadıysa", value: "not_answered" }]
                          : [{ label: "şuna eşitse", value: "equals" }, { label: "şuna eşit değilse", value: "not_equals" }, { label: "yanıtlandıysa", value: "answered" }, { label: "yanıtlanmadıysa", value: "not_answered" }];
                      const valueOptions = sourceQuestion?.choices?.map((choice) => ({ label: choice.text, value: choice.key })) ?? [];
                      return (
                        <div key={`condition-${conditionIndex}`} className="grid align-items-center m-0 p-2 surface-card border-round border-1 surface-border">
                          <div className="col-12 md:col-4 p-1">
                            <Dropdown value={condition.question_key} options={questions.map((question) => ({ label: question.text || "Başlıksız soru", value: question.key }))} onChange={(e) => updateRuleCondition(ruleIndex, conditionIndex, { question_key: e.value, operator: "answered", value: undefined })} disabled={conditionIndex === 0} placeholder="Soru seçin" className="w-full" tooltip={conditionIndex === 0 ? "Bu kural bu soruya bağlıdır." : undefined} />
                          </div>
                          <div className="col-12 md:col-4 p-1">
                            <Dropdown value={condition.operator} options={operatorOptions} onChange={(e) => updateRuleCondition(ruleIndex, conditionIndex, { operator: e.value, value: undefined })} className="w-full" />
                          </div>
                          <div className="col-10 md:col-3 p-1">
                            {hasValue && isChoice && (condition.operator === "contains_all" || condition.operator === "contains_any") ? (
                              <MultiSelect value={Array.isArray(condition.value) ? condition.value : condition.value == null ? [] : [condition.value]} options={valueOptions} optionLabel="label" optionValue="value" onChange={(e) => updateRuleCondition(ruleIndex, conditionIndex, { value: e.value })} placeholder="Seçenekler" className="w-full" />
                            ) : hasValue && isChoice ? (
                              <Dropdown value={condition.value} options={valueOptions} optionLabel="label" optionValue="value" onChange={(e) => updateRuleCondition(ruleIndex, conditionIndex, { value: e.value })} placeholder="Seçenek" className="w-full" />
                            ) : hasValue && sourceQuestion?.question_type === "number" ? (
                              <InputNumber value={condition.value == null ? null : Number(condition.value)} onValueChange={(e) => updateRuleCondition(ruleIndex, conditionIndex, { value: e.value ?? undefined })} className="w-full" inputClassName="w-full" />
                            ) : hasValue ? (
                              <InputText type={sourceQuestion?.question_type === "date" ? "date" : "text"} value={String(condition.value ?? "")} onChange={(e) => updateRuleCondition(ruleIndex, conditionIndex, { value: e.target.value })} placeholder="Karşılaştırma değeri" className="w-full" />
                            ) : <small className="text-500">Değer gerekmiyor</small>}
                          </div>
                          <div className="col-2 md:col-1 p-1 text-right"><Button icon="pi pi-times" text rounded severity="secondary" aria-label="Koşulu kaldır" disabled={rule.conditions.length < 2} onClick={() => updateRule(ruleIndex, { conditions: rule.conditions.filter((_, i) => i !== conditionIndex) })} /></div>
                        </div>
                      );
                    })}
                  </div>
                  {!isSectionRule && <Button label="Koşul ekle" icon="pi pi-plus" text size="small" className="mt-2" disabled={questions.length === 0} onClick={() => addRuleCondition(ruleIndex)} />}
                  <Divider />
                  <div className="grid align-items-center m-0">
                    <div className="col-12 md:col-4 p-1"><label className="block text-sm font-semibold mb-1">Eylem</label><Dropdown value={rule.action_type} options={actionOptions} onChange={(e) => updateRule(ruleIndex, { action_type: e.value, target_question_key: e.value === "hide_question" ? questions.find((question) => question.key !== rule.conditions[0]?.question_key)?.key : null, target_section_key: e.value === "goto_section" ? sections.find((section) => section.key !== (rule.source_section_key ?? logicQuestion?.section_key))?.key ?? null : null })} className="w-full" /></div>
                    {rule.action_type === "hide_question" && <div className="col-12 md:col-8 p-1"><label className="block text-sm font-semibold mb-1">Gizlenecek soru</label><Dropdown value={rule.target_question_key} options={questions.map((question) => ({ label: question.text || "Başlıksız soru", value: question.key }))} onChange={(e) => updateRule(ruleIndex, { target_question_key: e.value })} className="w-full" placeholder="Soru seçin" /></div>}
                    {rule.action_type === "goto_section" && <div className="col-12 md:col-8 p-1"><label className="block text-sm font-semibold mb-1">Hedef bölüm</label><Dropdown value={rule.target_section_key} options={sections.filter((section) => section.key !== (rule.source_section_key ?? logicQuestion?.section_key)).map((section) => ({ label: section.title, value: section.key }))} onChange={(e) => updateRule(ruleIndex, { target_section_key: e.value })} className="w-full" placeholder="Hedef bölüm" /></div>}
                  </div>
                </div>
                );
              })}
            </div>
          </Dialog>
          <Dialog
            header="Mevcut yanıtlar etkilenebilir"
            visible={structureChangeConfirmationVisible}
            onHide={() => setStructureChangeConfirmationVisible(false)}
            modal
            style={{ width: "min(520px, 94vw)" }}
            footer={
              <div className="flex justify-content-end gap-2">
                <Button label="Vazgeç" icon="pi pi-times" outlined severity="secondary" onClick={() => setStructureChangeConfirmationVisible(false)} />
                <Button label="Değişikliği Kaydet" icon="pi pi-exclamation-triangle" severity="warning" onClick={() => { setStructureChangeConfirmationVisible(false); void handleSave(true); }} />
              </div>
            }
          >
            <p className="text-700 m-0">
              Bu ankette {responseCount} kayıtlı yanıt var. Soru veya bölüm yapısı değiştirilirse mevcut cevap içerikleri silinebilir ve raporlar etkilenebilir. Değişikliği yine de kaydetmek istiyor musunuz?
            </p>
          </Dialog>
        </TabPanel>

        {/* SEKME 3: CANLI ÖNİZLEME */}
        <TabPanel header="Canlı Önizleme" leftIcon="pi pi-eye mr-2">
          <div className="surface-card border-round-xl p-4 shadow-1 border-1 surface-border mt-2">
            <div className="mb-4 text-center">
              <Tag value="Önizleme Modu" severity="info" className="mb-2" />
              <h2 className="text-900 font-bold text-3xl m-0">{title || "Anket Başlığı"}</h2>
              {description && <p className="text-500 mt-2 max-w-30rem mx-auto">{description}</p>}
            </div>

            {/* Bölüm Seçici (Önizleme İçin) */}
            <div className="surface-50 p-3 border-round-lg mb-4 flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="font-semibold text-900 text-sm">
                Bölüm: {currentPreviewSection?.title} ({currentSectionIndex + 1} / {sections.length})
              </span>
              <div className="flex gap-2">
                {sections.map((s, i) => (
                  <Button
                    key={s.key}
                    label={`Bölüm ${i + 1}`}
                    size="small"
                    outlined={s.key !== previewSectionKey}
                    onClick={() => setPreviewSectionKey(s.key)}
                  />
                ))}
              </div>
            </div>

            <div className="flex align-items-center justify-content-between mb-2 text-xs font-semibold text-600">
              <span>Bölüm İlerlemesi ({currentSectionIndex + 1} / {sections.length})</span>
              <span>%{Math.round(((currentSectionIndex + 1) / (sections.length || 1)) * 100)}</span>
            </div>
            <ProgressBar value={Math.round(((currentSectionIndex + 1) / (sections.length || 1)) * 100)} className="mb-4" style={{ height: "6px" }} />

            {/* Önizleme Soruları */}
            <div className="flex flex-column gap-3">
              {previewQuestions.length === 0 ? (
                <div className="text-center p-5 text-500">Bu bölümde henüz gösterilecek soru yok.</div>
              ) : (
                previewQuestions.map((q, idx) => (
                  <Card key={q.id ?? idx} className="shadow-1 border-round-xl">
                    <div className="flex align-items-start gap-2 mb-3">
                      <span className="font-bold text-primary mr-1">{idx + 1}.</span>
                      <div className="flex-1">
                        <span className="font-semibold text-900">
                          {q.text} {q.is_required && <span className="text-red-500">*</span>}
                        </span>
                        {q.help_text && <small className="text-500 block mt-1">{q.help_text}</small>}
                      </div>
                    </div>

                    {q.question_type === "text" && (
                      <InputTextarea
                        rows={3}
                        className="w-full text-sm"
                        placeholder="Katılımcı serbest metin cevabını buraya yazacak (Önizlemede test edebilirsiniz)..."
                        value={previewAnswers[idx] ?? ""}
                        onChange={(e) => setPreviewAnswers((prev) => ({ ...prev, [idx]: e.target.value }))}
                        autoResize
                      />
                    )}

                    {q.question_type === "date" && <InputText type="date" className="w-full md:w-20rem" value={previewAnswers[idx] ?? ""} onChange={(e) => setPreviewAnswers((prev) => ({ ...prev, [idx]: e.target.value }))} />}

                    {q.question_type === "number" && <InputNumber className="w-full md:w-20rem" inputClassName="w-full" min={q.config?.min ?? undefined} max={q.config?.max ?? undefined} value={previewAnswers[idx] ?? null} onValueChange={(e) => setPreviewAnswers((prev) => ({ ...prev, [idx]: e.value ?? null }))} />}

                    {q.question_type === "single_choice" && (
                      <div className="flex flex-column gap-2">
                        {q.choices?.map((c, ci) => (
                          <div key={ci} className="flex align-items-center gap-2">
                            <RadioButton
                              inputId={`prev-choice-${ci}`}
                              name={`prev-q-${idx}`}
                              value={c.text}
                              checked={previewAnswers[idx] === c.text}
                              onChange={() => setPreviewAnswers((prev) => ({ ...prev, [idx]: c.text }))}
                            />
                            <label htmlFor={`prev-choice-${ci}`} className="cursor-pointer text-900 text-sm">
                              {c.text}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                    {q.question_type === "dropdown" && (
                      <Dropdown
                        options={q.choices?.map((c) => ({ label: c.text, value: c.text }))}
                        placeholder="Bir seçenek belirleyin"
                        className="w-full md:w-20rem"
                        value={previewAnswers[idx]}
                        onChange={(e) => setPreviewAnswers((prev) => ({ ...prev, [idx]: e.value }))}
                      />
                    )}

                    {q.question_type === "multiple_choice" && (
                      <div className="flex flex-column gap-2">
                        {q.choices?.map((c, ci) => (
                          <div key={ci} className="flex align-items-center gap-2">
                            <Checkbox inputId={`prev-multi-${ci}`} checked={false} disabled />
                            <label htmlFor={`prev-multi-${ci}`} className="text-900 text-sm">
                              {c.text}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                    {q.question_type === "rating" && (
                      <div className="flex align-items-center gap-2">
                        <Rating value={previewAnswers[idx] ?? 0} onChange={(e) => setPreviewAnswers((prev) => ({ ...prev, [idx]: e.value }))} cancel={false} />
                        <span className="text-500 text-xs">Yıldız ile değerlendirin</span>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>

            <div className="flex justify-content-between mt-4">
              <Button
                label="Önceki Bölüm"
                icon="pi pi-arrow-left"
                outlined
                disabled={currentSectionIndex <= 0}
                onClick={() => setPreviewSectionKey(sections[currentSectionIndex - 1].key)}
              />
              <Button
                label={currentSectionIndex < sections.length - 1 ? "Sonraki Bölüm" : "Anketi Tamamla"}
                icon={currentSectionIndex < sections.length - 1 ? "pi pi-arrow-right" : "pi pi-check"}
                iconPos="right"
                disabled={currentSectionIndex >= sections.length - 1}
                onClick={() => setPreviewSectionKey(sections[currentSectionIndex + 1].key)}
              />
            </div>
          </div>
        </TabPanel>
      </TabView>

      {/* ── Alt Sabit Eylem Çubuğu ──────────────────────────────────── */}
      <div className="surface-card border-round-xl shadow-1 p-3 mt-4 border-1 surface-border flex justify-content-between align-items-center flex-wrap gap-2">
        <span className="text-500 text-sm">
          {isEditMode ? "Değişiklikleri kaydetmeyi unutmayın." : "Tüm ayarları tamamladıktan sonra anketi oluşturun."}
        </span>
        <div className="flex gap-2">
          <Button
            label="İptal"
            icon="pi pi-times"
            outlined
            severity="secondary"
            onClick={() => navigate("/admin/dinamik-anket-yonetim")}
            disabled={saving}
          />
          <Button
            label={isEditMode ? "Güncellemeleri Kaydet" : "Anketi Oluştur ve Yayınla"}
            icon="pi pi-save"
            loading={saving}
            onClick={() => { void handleSave(); }}
            className="font-semibold shadow-1"
          />
        </div>
      </div>

      {/* ── Soru Ekleme / Düzenleme Modali (Google Forms Tarzı) ─────────────────────────────── */}
      <Dialog
        header={
          <div className="flex align-items-center justify-content-between w-full pr-3 flex-wrap gap-2">
            <span className="font-semibold text-900 text-lg">
              {editingQuestionIndex === null ? "Yeni Soru Ekle" : "Soruyu Düzenle"}
            </span>
            <div className="flex align-items-center gap-2">
              <span className="text-500 text-xs font-medium">Bölüm:</span>
              <Dropdown
                value={editingQuestion.section_key}
                options={sections.map((section) => ({ label: section.title, value: section.key }))}
                onChange={(e) =>
                  setEditingQuestion((prev) => ({
                    ...prev,
                    section_key: e.value,
                    order: questions.filter((question) => question.section_key === e.value).length,
                  }))
                }
                className="p-inputtext-sm text-xs"
                style={{ minWidth: "150px" }}
              />
            </div>
          </div>
        }
        visible={soruDialogVisible}
        onHide={() => setSoruDialogVisible(false)}
        style={{ width: "min(680px, 95vw)" }}
        className="p-fluid"
        footer={
          <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full pt-2 border-top-1 surface-border">
            <div className="flex align-items-center gap-2">
              <Button
                label="İptal"
                text
                severity="secondary"
                onClick={() => setSoruDialogVisible(false)}
              />
              <Button
                label={`Mantık${editingQuestion.key ? ` (${rules.filter((rule) => rule.conditions[0]?.question_key === editingQuestion.key).length})` : ""}`}
                icon="pi pi-share-alt"
                outlined
                size="small"
                tooltip={editingQuestionIndex === null ? "Önce soruyu ekleyin, sonra mantık kurallarını düzenleyin." : "Bu soruya bağlı koşulları düzenle"}
                disabled={editingQuestionIndex === null || !editingQuestion.key}
                onClick={() => {
                  setLogicQuestionKey(editingQuestion.key ?? null);
                  setLogicDialogVisible(true);
                }}
              />
            </div>
            <div className="flex align-items-center gap-3">
              <div className="flex align-items-center gap-2">
                <span className="text-sm font-medium text-700 select-none">Gerekli</span>
                <InputSwitch
                  id="modal-is-required"
                  checked={!!editingQuestion.is_required}
                  onChange={(e) => setEditingQuestion((prev) => ({ ...prev, is_required: e.value }))}
                />
              </div>

              <div className="border-right-1 surface-border h-2rem hidden sm:block" />

              <Menu
                model={[
                  {
                    label: "Açıklama",
                    icon: (showHelpText || editingQuestion.help_text) ? "pi pi-check" : undefined,
                    command: () => {
                      if (showHelpText || editingQuestion.help_text) {
                        setShowHelpText(false);
                        setEditingQuestion((prev) => ({ ...prev, help_text: "" }));
                      } else {
                        setShowHelpText(true);
                      }
                    },
                  },
                ]}
                popup
                ref={dotsMenuRef}
              />
              <Button
                type="button"
                icon="pi pi-ellipsis-v"
                rounded
                text
                severity="secondary"
                tooltip="Diğer seçenekler"
                tooltipOptions={{ position: "top" }}
                onClick={(e) => dotsMenuRef.current?.toggle(e)}
              />

              <Button
                label={editingQuestionIndex === null ? "Soru Ekle" : "Kaydet"}
                icon={editingQuestionIndex === null ? "pi pi-plus" : "pi pi-check"}
                onClick={saveQuestion}
                className="font-semibold px-4"
              />
            </div>
          </div>
        }
      >
        <div className="flex flex-column gap-3 pt-2">
          {/* Soru Metni & Soru Tipi (Tek Satırda Yan Yana) */}
          <div className="grid align-items-start m-0">
            <div className="col-12 sm:col-8 p-1">
              <label className="block text-xs font-bold text-600 mb-1">
                SORU METNİ <span className="text-red-500">*</span>
              </label>
              <InputTextarea
                value={editingQuestion.text ?? ""}
                onChange={(e) => setEditingQuestion((prev) => ({ ...prev, text: e.target.value }))}
                placeholder="Örn: Görüş ve önerilerinizi detaylı olarak belirtiniz..."
                rows={2}
                autoResize
                className="w-full text-base font-medium surface-50 border-1 surface-border p-2 border-round-md"
                autoFocus
              />
            </div>
            <div className="col-12 sm:col-4 p-1">
              <label className="block text-xs font-bold text-600 mb-1">SORU TİPİ</label>
              <Dropdown
                value={editingQuestion.question_type}
                options={QUESTION_TYPES}
                optionLabel="label"
                optionValue="value"
                itemTemplate={(option) => (
                  <div className="flex align-items-center gap-2 text-sm">
                    <i className={option.icon} style={{ color: option.color }} />
                    <span>{option.label}</span>
                  </div>
                )}
                valueTemplate={(option) => {
                  if (!option) return null;
                  return (
                    <div className="flex align-items-center gap-2 text-sm">
                      <i className={option.icon} style={{ color: option.color }} />
                      <span>{option.label}</span>
                    </div>
                  );
                }}
                onChange={(e) =>
                  setEditingQuestion((prev) => ({
                    ...prev,
                    question_type: e.value,
                    choices:
                      ["text", "date", "number", "rating"].includes(e.value)
                        ? []
                        : (prev.choices && prev.choices.length > 0)
                        ? prev.choices
                        : [emptyChoice("Seçenek 1")],
                  }))
                }
                className="w-full text-sm p-inputtext-sm"
              />
            </div>
          </div>

          {editingQuestion.question_type === "number" && (
            <div className="grid m-0 surface-50 border-round-lg p-2">
              <div className="col-12 sm:col-6">
                <label className="block text-sm font-semibold mb-1">En düşük değer</label>
                <InputNumber value={editingQuestion.config?.min ?? null} onValueChange={(e) => setEditingQuestion((prev) => ({ ...prev, config: { ...prev.config, min: e.value ?? null } }))} className="w-full" inputClassName="w-full" />
              </div>
              <div className="col-12 sm:col-6">
                <label className="block text-sm font-semibold mb-1">En yüksek değer</label>
                <InputNumber value={editingQuestion.config?.max ?? null} onValueChange={(e) => setEditingQuestion((prev) => ({ ...prev, config: { ...prev.config, max: e.value ?? null } }))} className="w-full" inputClassName="w-full" />
              </div>
            </div>
          )}

          {/* Açıklama / İpucu Metni (3 noktadan 'Açıklama' tıklandığında görünür) */}
          {(showHelpText || editingQuestion.help_text) && (
            <div className="px-1 flex align-items-center gap-2">
              <InputText
                value={editingQuestion.help_text ?? ""}
                onChange={(e) => setEditingQuestion((prev) => ({ ...prev, help_text: e.target.value }))}
                placeholder="Açıklama / İpucu metni..."
                className="w-full text-sm p-inputtext-sm text-600 surface-50"
              />
              <Button
                icon="pi pi-times"
                text
                rounded
                size="small"
                severity="secondary"
                tooltip="Açıklamayı kaldır"
                onClick={() => {
                  setShowHelpText(false);
                  setEditingQuestion((prev) => ({ ...prev, help_text: "" }));
                }}
              />
            </div>
          )}

          {/* Seçenek Listesi (Google Forms tarzı) */}
          {["single_choice", "dropdown", "multiple_choice"].includes(editingQuestion.question_type) && (
            <div className="flex flex-column gap-2 mt-2 px-1">
              {(editingQuestion.choices ?? []).map((choice, cIndex) => (
                <div
                  key={cIndex}
                  className={`flex align-items-center gap-2 flex-wrap sm:flex-nowrap p-1 border-round-md transition-colors ${
                    choice.is_correct ? "surface-100 border-1 border-green-400" : ""
                  }`}
                >
                  <div className="flex align-items-center gap-2 flex-1" style={{ minWidth: "220px" }}>
                    {editingQuestion.question_type === "multiple_choice" ? (
                      <i className="pi pi-stop text-400 text-sm" />
                    ) : (
                      <i className="pi pi-circle text-400 text-sm" />
                    )}
                    <InputText
                      value={choice.text}
                      onChange={(e) => updateChoice(cIndex, e.target.value)}
                      placeholder={`Seçenek ${cIndex + 1}`}
                      className="flex-1 p-inputtext-sm"
                    />
                    {/* Doğru Cevap Onay Butonu (Google Forms Tarzı) */}
                    <Button
                      type="button"
                      icon="pi pi-check"
                      rounded
                      text={!choice.is_correct}
                      severity={choice.is_correct ? "success" : "secondary"}
                      size="small"
                      tooltip={
                        choice.is_correct
                          ? "Doğru Cevap (İptal etmek için tıklayın)"
                          : "Bu seçeneği Doğru Cevap olarak işaretle"
                      }
                      tooltipOptions={{ position: "top" }}
                      onClick={() => toggleChoiceCorrect(cIndex)}
                    />
                    <Button
                      icon="pi pi-times"
                      rounded
                      text
                      size="small"
                      severity="secondary"
                      tooltip="Kaldır"
                      onClick={() => removeChoice(cIndex)}
                    />
                  </div>

                </div>
              ))}

              {/* Seçenek Ekle Satırı */}
              <div
                className="flex align-items-center gap-2 py-2 px-1 cursor-pointer text-500 hover:text-primary transition-colors select-none"
                onClick={addChoice}
              >
                {editingQuestion.question_type === "multiple_choice" ? (
                  <i className="pi pi-stop text-400 text-sm" />
                ) : (
                  <i className="pi pi-circle text-400 text-sm" />
                )}
                <span className="text-sm font-medium">Seçenek ekle</span>
              </div>
            </div>
          )}

          {/* Serbest Metin Bilgilendirme */}
          {editingQuestion.question_type === "text" && (
            <div className="surface-50 border-round-lg p-3 border-1 surface-border flex align-items-center gap-3">
              <div className="bg-purple-100 p-2 border-round text-purple-700 flex align-items-center justify-content-center">
                <i className="pi pi-align-left text-xl" />
              </div>
              <div>
                <strong className="text-900 block text-sm mb-1">Serbest Metin Yanıt Alanı</strong>
                <small className="text-600 block line-height-2">
                  Katılımcılara bu soruyu cevaplarken kendi cümleleriyle metin girebilecekleri serbest bir yazı kutusu (textarea) sunulur. Seçenek eklemenize gerek yoktur.
                </small>
              </div>
            </div>
          )}

          {/* Derecelendirme İpucu */}
          {editingQuestion.question_type === "rating" && (
            <div className="surface-50 border-round-md p-3 border-1 surface-border flex align-items-center gap-3">
              <Rating value={5} readOnly cancel={false} />
              <span className="text-600 text-sm">Katılımcılar 1 - 5 yıldız arası puanlama yapacak.</span>
            </div>
          )}
        </div>
      </Dialog>

      {/* Bölüm ve Akış Bilgilendirme Modalı */}
      <Dialog
        header={
          <div className="flex align-items-center gap-2 text-primary font-bold text-lg">
            <i className="pi pi-info-circle text-xl" />
            <span>Ankette Bölüm (Section) Mantığı ve Akış</span>
          </div>
        }
        visible={sectionInfoVisible}
        onHide={() => setSectionInfoVisible(false)}
        style={{ width: "560px", maxWidth: "95vw" }}
        breakpoints={{ "960px": "75vw", "641px": "92vw" }}
        modal
        dismissableMask
        footer={
          <Button label="Anladım" icon="pi pi-check" onClick={() => setSectionInfoVisible(false)} autoFocus />
        }
      >
        <div className="flex flex-column gap-3 text-700 line-height-3 pt-2">
          <p className="m-0 text-sm text-600">
            Bölümler (Sections), anketinizi adımlara bölen ve akıllı yönlendirmeler yapmanızı sağlayan sayfa yapılarıdır:
          </p>

          <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-start gap-3">
            <div className="bg-primary-50 p-2 border-round text-primary flex align-items-center justify-content-center">
              <i className="pi pi-clone text-xl" />
            </div>
            <div>
              <strong className="text-900 block mb-1">1. Çok Adımlı Sayfalama (Sayfa Sayfa İlerleme)</strong>
              <small className="text-600 block line-height-2">
                Tüm soruları tek bir uzun sayfada göstermek yerine, her bölümü ayrı bir sayfa/adım yapar. Katılımcı "İleri" butonuna basarak adım adım anketi tamamlar.
              </small>
            </div>
          </div>

          <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-start gap-3">
            <div className="bg-blue-50 p-2 border-round text-blue-500 flex align-items-center justify-content-center">
              <i className="pi pi-folder text-xl" />
            </div>
            <div>
              <strong className="text-900 block mb-1">2. Konu Gruplaması</strong>
              <small className="text-600 block line-height-2">
                Soruları mantıksal başlıklara ayırır (Örn: <em>1. Adım: Genel Bilgiler</em>, <em>2. Adım: Enerji Tüketimi</em>, <em>3. Adım: Değerlendirme</em>).
              </small>
            </div>
          </div>

          <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-start gap-3">
            <div className="bg-teal-50 p-2 border-round text-teal-600 flex align-items-center justify-content-center">
              <i className="pi pi-sitemap text-xl" />
            </div>
            <div>
              <strong className="text-900 block mb-1">3. Dallanma & Koşullu Yönlendirme (Skip Logic)</strong>
              <small className="text-600 block line-height-2">
                Katılımcının bir seçeneğe verdiği cevaba göre farklı bir bölüme atlamasını veya anketi erken bitirmesini sağlar (Örn: "Ar-Ge Birimi Var mı?": "Hayır" ise Ar-Ge bölümünü göstermeden geç).
              </small>
            </div>
          </div>

          <div className="surface-100 p-2 border-round text-xs text-500 flex align-items-center gap-2">
            <i className="pi pi-lightbulb text-orange-500 text-sm" />
            <span>Basit ve tek adımlı bir anket istiyorsanız tek bölüm ("Genel") yeterlidir. Ekstra bölüm açmak zorunda değilsiniz.</span>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DynamikAnketOlustur;
